import { getFlagCountsForProjects } from '@gonfalon/openapi/operations/getFlagCountsForProjects';
import { getFlagDefaultsByProject } from '@gonfalon/openapi/operations/getFlagDefaultsByProject';
import { getFollowersByProjEnv } from '@gonfalon/openapi/operations/getFollowersByProjEnv';
import { getProject, type GetProjectQueryParams } from '@gonfalon/openapi/operations/getProject';
import { getProjects, type GetProjectsQueryParams } from '@gonfalon/openapi/operations/getProjects';
import { getProjectsInternal } from '@gonfalon/openapi/operations/getProjectsInternal';
import { getRandomizationSettings } from '@gonfalon/openapi/operations/getRandomizationSettings';
import { patchProject } from '@gonfalon/openapi/operations/patchProject';
import { postProject } from '@gonfalon/openapi/operations/postProject';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { createInfiniteQueryOptions } from '../internal/createInfiniteQueryOptions';
import { createMutationHook } from '../internal/createMutationHook';
import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';
import type { JSONPatch } from '../types';

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const projects = createQueryKeys('projects', {
  list: ({ params }: { params?: GetProjectsQueryParams }) => ({
    // Working around some limitations of query-key-factory here.
    //
    // [params] | null is not a valid type for queryKey, so we add a
    // placeholder string ('list') to satisfy the types.
    queryKey: params ? ['list', params] : ['list'],
    queryFn: async () => reactQueryResponseAdapter(getProjects({ query: params })),
  }),
  /**
   * @deprecated please use projectsDetail query instead
   */
  detail: ({ projectKey, params }: { projectKey: string; params?: GetProjectQueryParams }) => ({
    queryKey: params ? [projectKey, params] : [projectKey],
    queryFn: async () => reactQueryResponseAdapter(getProject({ projectKey, query: params })),
    contextQueries: {
      flagDefaults: {
        queryKey: null,
        queryFn: async ({ signal }) => reactQueryResponseAdapter(getFlagDefaultsByProject({ projectKey }, { signal })),
      },
      randomizationSettings: {
        queryKey: null,
        queryFn: async ({ signal }) => reactQueryResponseAdapter(getRandomizationSettings({ projectKey }, { signal })),
      },
      followers({ environmentKey }: { environmentKey: string }) {
        return {
          queryKey: [environmentKey],
          queryFn: async ({ signal }) =>
            reactQueryResponseAdapter(getFollowersByProjEnv({ projectKey, environmentKey }, { signal })),
        };
      },
    },
  }),
  update: ({ projectKey, input }: { projectKey: string; input: { patch: JSONPatch } }) => ({
    queryKey: [projectKey],
    queryFn: async ({ signal }) =>
      reactQueryResponseAdapter(patchProject({ projectKey, body: input.patch }, { signal })),
  }),
});

export const projectsList = projects.list;

export const getFlagCountsForProjectsQuery = createQueryOptions(getFlagCountsForProjects);
export const getProjectQuery = createQueryOptions(getProject);
export const getProjectsQuery = createQueryOptions(getProjects);
export const getInfiniteProjectsQuery = createInfiniteQueryOptions(getProjects);
export const getInfiniteProjectsInternalQuery = createInfiniteQueryOptions(getProjectsInternal);

export const usePostProjectMutation = createMutationHook(postProject, {
  onSuccess: async (createdProject) => {
    const queryClient = getQueryClient();
    await Promise.all([
      // Every projects list, not just the sorted one the manage-projects table uses. Callers also
      // read the account's project count off this query, and a stale count there decides whether
      // creating another project is allowed at all.
      queryClient.invalidateQueries({ queryKey: getProjectsQuery.partialQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getProjectQuery.partialQueryKey({ projectKey: createdProject.key }) }),
      queryClient.invalidateQueries({ queryKey: getFlagCountsForProjectsQuery.partialQueryKey() }),
    ]);
  },
});
